import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchNotificationPreferences,
  updateNotificationPreference,
  type NotificationPreference,
} from '@/api/notifications';
import type { NotificationEvent } from '@/types/generated';

/**
 * What reaches you, and how.
 *
 * ONE COLUMN OF SWITCHES, NOT TWO. In-app is always on and is stated as fact rather than offered
 * as a disabled control — the bell is the record of what happened to your work, and an account
 * that can silence it produces a support conversation beginning "I was never told". Email is the
 * channel that interrupts, so email is the one that can be refused.
 *
 * Saved per switch rather than behind a Save button. There is no cross-field validity here, so a
 * form would only add a state in which the screen and the server disagree.
 */
export function NotificationSettingsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: fetchNotificationPreferences,
  });

  const save = useMutation({
    mutationFn: ({ event, email }: { event: NotificationEvent; email: boolean }) =>
      updateNotificationPreference(event, email),
    onSuccess: (updated) => {
      // The server returns the full, defaults-filled picture, so it replaces the cache outright
      // rather than being merged into it.
      queryClient.setQueryData(['notification-preferences'], updated);
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    return <Alert severity="error">Could not load your notification settings.</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h1" gutterBottom>
          Notifications
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Everything here appears in the bell. Email is for the ones worth interrupting you for.
        </Typography>
      </Box>

      {save.isError && (
        <Alert severity="error">That change was not saved. Please try again.</Alert>
      )}

      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          {data.map((preference: NotificationPreference, index: number) => (
            <Box key={preference.event}>
              {index > 0 && <Divider />}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ px: 3, py: 2 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {preference.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {preference.description}
                  </Typography>
                </Box>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Switch
                    checked={preference.email}
                    disabled={save.isPending}
                    onChange={(e) =>
                      save.mutate({ event: preference.event, email: e.target.checked })
                    }
                    inputProps={{ 'aria-label': `Email me about ${preference.label}` }}
                  />
                </Stack>
              </Stack>
            </Box>
          ))}
        </CardContent>
      </Card>

      <Typography variant="caption" color="text.secondary">
        These apply everywhere you work. Which notifications you receive still depends on your role
        at each organisation — an approval request only reaches you where you are an approver.
      </Typography>
    </Stack>
  );
}
